// ─── Fiche INFO d'un événement (lecture seule) ────────────────────
// Extrait d'EcranCalendrier (découpage). Feuille 82 % : en-tête pastille +
// badges, carte de détails, jours effectifs de la récurrence, jours ignorés
// (balayage = rétablir), note, pièces jointes. Les actions (✏️ / ⇄ / 🗑️)
// remontent au parent via callbacks — la logique de navigation reste là-bas.
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, LigneSwipe, PiecesJointes, useUI, useT, HIT, demanderItineraire } from '@mop/core';
import { parseHeureMin } from './services/affichageConfig';
import { JOURS_SEM } from './services/evenements';
import { emojiCategorie, labelCategorie, typeEvenement, labelTypeEvenement } from './data/categories';

export default function FicheEvenement({
  evtInfo, edit, calId, calendriers, MOIS, joursL, repetLabel,
  onFermer, onEditer, onDeplacer, onSupprimer, onRetablirException,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const evA = evtInfo?.ev;
  const action = (evA && edit && !evA.isEdt)
    ? { icone: '✏️', label: t('commun.modifier'), onPress: onEditer }
    : undefined;
  // 🗑️ SÉPARÉ de ✏️ : simple = confirmation de suppression ; récurrent =
  // choix « ignorer ce jour / supprimer toute la série » (géré au parent).
  const deplacable = !!evA && (evA.isRecurrent || (!evA.estPlage && !evA.dateFin));
  const boutons = (evA && edit && !evA.isEdt) ? (
    <>
      {deplacable && (
        <TouchableOpacity hitSlop={HIT} accessibilityLabel={t('calendrier.deplacerOccurrence')} onPress={onDeplacer}>
          <Text style={{ fontSize: 19, color: UI.accent, fontWeight: '800' }}>⇄</Text>
        </TouchableOpacity>
      )}
      <TouchableOpacity hitSlop={HIT} accessibilityLabel={t('commun.supprimer')} onPress={onSupprimer}>
        <Text style={{ fontSize: 17 }}>🗑️</Text>
      </TouchableOpacity>
    </>
  ) : undefined;

  return (
    <Feuille visible={!!evtInfo} titre={t('historique.type_evenement')} emoji="📌" hauteur={0.82}
      onFermer={onFermer} action={action} boutons={boutons}>
      {evtInfo && (() => {
        const ev = evtInfo.ev;
        const jour = (s0) => { const p = (s0 || '').split('-'); return p.length === 3 ? `${+p[2]} ${MOIS[+p[1] - 1]} ${p[0]}` : ''; };
        const nomJour = (s0) => { const p = (s0 || '').split('-').map(Number); if (p.length !== 3) return ''; const jl = t('commun.joursLongs'); return jl[(new Date(p[0], p[1] - 1, p[2]).getDay() + 6) % 7] || ''; };
        const estP = !!ev.estPlage;
        // Début / fin : pour un RÉCURRENT, ce sont les bornes de la RÉCURRENCE ;
        // sinon celles de l'événement lui-même (fin omise si mono-jour).
        const rd = ev.isRecurrent ? (ev.recDef || {}) : null;
        const debutTxt = rd ? jour(rd.dateDebut) : (estP ? jour(ev.plageSourceDk || evtInfo.dk) : jour(evtInfo.dk));
        const finTxt = rd ? (rd.dateFin ? jour(rd.dateFin) : t('calendrier.sansFin'))
          : (ev.dateFin ? jour(ev.dateFin) : null);
        const hd = estP ? ev.plageHeureDebut : ev.heure;
        const hf = estP ? ev.plageHeureFin : ev.heureFin;
        const heureTxt = hd ? `${hd}${hf ? ` – ${hf}` : ''}` : t('calendrier.touteLaJournee');
        // Durée lisible (événement d'un jour avec début + fin).
        let dureeTxt = null;
        if (!estP && hd && hf) {
          const d0 = parseHeureMin(hd), d1 = parseHeureMin(hf);
          if (d0 != null && d1 != null && d1 > d0) {
            const mn = d1 - d0;
            dureeTxt = mn >= 60 ? `${Math.floor(mn / 60)} h${mn % 60 ? ` ${String(mn % 60).padStart(2, '0')}` : ''}` : `${mn} min`;
          }
        }
        const calEv = calendriers.find(c => c.id === (ev._calId || calId));
        const couleurEv = ev._couleur || calEv?.couleur || UI.accent;
        // Lieu résolu EN DIRECT (par lieuId) → nom + adresse suivent la page Lieux
        // (aucune copie figée sur l'événement). Repli sur le nom mémorisé si supprimé.
        const lo = ev.lieuId ? (calEv?.lieux || []).find(l => l.id === ev.lieuId) : null;
        const rows = [
          { ic: '📅', lbl: t('calendrier.dateDebut'), val: debutTxt },
          ...(finTxt ? [{ ic: '🏁', lbl: t('finances.dateFin'), val: finTxt }] : []),
          { ic: '🕒', lbl: t('calendrier.ficheHoraire'), val: heureTxt },
          ...(dureeTxt ? [{ ic: '⏱️', lbl: t('calendrier.ficheDuree'), val: dureeTxt }] : []),
          ...((lo || ev.lieu) ? [{ ic: '📍', lbl: t('edt.emplacement'), val: lo?.nom || ev.lieu, onPress: () => demanderItineraire({ lat: lo?.lat, lng: lo?.lng, adresse: lo?.adresse || ev.lieu },
            { titre: t('lieux.itineraire'), annuler: t('commun.annuler') }) }] : []),
          { ic: '📎', lbl: t('calendrier.ficheType'), val: labelTypeEvenement(typeEvenement(ev), t) },
          // Champs « a la carte » de l'evenement : affiches tels quels, dans
          // l'ordre ou ils ont ete ajoutes (comme la fiche contact).
          ...((ev.champs || []).filter(c => c && c.valeur)
            .map(c => ({ ic: c.emoji || '▫️', lbl: c.nom || c.cle, val: String(c.valeur) }))),
          // Règle de récurrence : type + intervalle seulement (les dates sont en
          // haut, les jours dans la rangée de pastilles sous la carte).
          ...(rd ? [{ ic: '🔁', lbl: t('calendrier.repetition'), val: (() => {
            const k = Number(rd.intervalle) || 1;
            return (rd.type === 'hebdo' && k > 1) ? t('calendrier.repetNSemaines', { n: k })
              : (rd.type === 'mensuel' && k > 1) ? t('calendrier.repetNMois', { n: k })
              : (rd.type === 'quotidien' && k > 1) ? t('calendrier.repetNJours', { n: k })
              : repetLabel(rd.type || 'quotidien');
          })() }] : []),
          // Copie déplacée : origine → jour actuel.
          ...(ev.estDeplace && ev.deplaceDe ? [{ ic: '⇄', lbl: t('calendrier.ficheDeplace'), val: `${jour(ev.deplaceDe)} → ${jour(evtInfo.dk)}` }] : []),
          ...(calEv ? [{ ic: calEv.emoji || '🗓️', lbl: t('calendrier.ficheCalendrier'), val: calEv.nom || '' }] : []),
        ];
        return (
          <View>
            {/* En-tête : pastille couleur de l'événement + titre + badges. */}
            <View style={s.ficheTete}>
              <View style={[s.fichePastille, { backgroundColor: couleurEv }]}>
                <Text style={s.fichePastilleTxt}>{emojiCategorie(ev.categorie) || '📅'}</Text>
              </View>
              <View style={{ flex: 1 }}>
                <Text style={s.ficheTitre}>{ev.titre}</Text>
                <View style={s.ficheBadges}>
                  {!!ev.categorie && (
                    <View style={[s.ficheBadge, { backgroundColor: couleurEv + '22' }]}>
                      <Text style={[s.ficheBadgeTxt, { color: couleurEv }]}>{labelCategorie(ev.categorie, t)}</Text>
                    </View>
                  )}
                  {ev.isRecurrent && <View style={s.ficheBadge}><Text style={s.ficheBadgeTxt}>{t('calendrier.badgeRecurrent')}</Text></View>}
                  {estP && <View style={s.ficheBadge}><Text style={s.ficheBadgeTxt}>{t('calendrier.badgePlage')}</Text></View>}
                </View>
              </View>
            </View>

            {/* Détails : carte à rangées étiquetées. */}
            <View style={s.ficheCarte}>
              {rows.map((r, i) => {
                const contenu = (
                  <>
                    <Text style={s.ficheRowIc}>{r.ic}</Text>
                    <Text style={s.ficheRowLbl}>{r.lbl}</Text>
                    <Text style={[s.ficheRowVal, r.onPress && { color: UI.accent, fontWeight: '700' }]} numberOfLines={2}>{r.val}{r.onPress ? '  🧭' : ''}</Text>
                  </>
                );
                return r.onPress ? (
                  <TouchableOpacity key={i} style={[s.ficheRow, i > 0 && s.ficheRowSep]} activeOpacity={0.7} onPress={r.onPress}>{contenu}</TouchableOpacity>
                ) : (
                  <View key={i} style={[s.ficheRow, i > 0 && s.ficheRowSep]}>{contenu}</View>
                );
              })}
            </View>

            {/* Jours effectifs de la récurrence : les 7 pastilles, comme à
                l'édition (masquée si les jours varient — quotidien espacé,
                mensuel au quantième). */}
            {rd && (() => {
              const k = Number(rd.intervalle) || (rd.type === 'bimensuel' ? 2 : 1);
              const wdDe = (dk0) => { const p = (dk0 || '').split('-').map(Number); return p.length === 3 ? new Date(p[0], p[1] - 1, p[2]).getDay() : null; };
              let js = null;
              if (rd.type === 'hebdo' || rd.type === 'bimensuel') js = (rd.joursSemaine && rd.joursSemaine.length) ? rd.joursSemaine : [wdDe(rd.dateDebut)].filter(x => x != null);
              else if (rd.type === 'quotidien' && k === 1) js = [0, 1, 2, 3, 4, 5, 6];
              else if (rd.type === 'mensuel' && rd.joursSemaine?.length) js = rd.joursSemaine;
              if (!js || !js.length) return null;
              return (
                <View style={s.ficheJours}>
                  {JOURS_SEM.map((j, ji) => {
                    const on = js.includes(j.i);
                    return (
                      <View key={ji} style={[s.ficheJour, on && s.ficheJourOn]}>
                        <Text style={[s.ficheJourTxt, on && s.ficheJourTxtOn]}>{joursL[ji]}</Text>
                      </View>
                    );
                  })}
                </View>
              );
            })()}

            {/* Jours IGNORÉS de la série : liste standard, balayage = rétablir. */}
            {rd && (ev.recExceptions || []).length > 0 && (
              <View style={{ marginTop: 14 }}>
                <Text style={s.lbl}>{t('calendrier.joursIgnores')}</Text>
                {[...ev.recExceptions].sort().map(x => (
                  <LigneSwipe key={x} espacement={7} enabled={edit}
                    onSupprimer={edit ? () => onRetablirException(ev, x) : undefined}>
                    <View style={s.excLigne}>
                      {(() => {
                        const dep = (ev.deplacements || {})[x];
                        return dep
                          ? <Text style={s.excTxt}>⇄ {nomJour(x)} {jour(x)}  →  {jour(dep.dk)}</Text>
                          : <Text style={s.excTxt}>🚫 {nomJour(x)} {jour(x)}</Text>;
                      })()}
                    </View>
                  </LigneSwipe>
                ))}
                {edit && <Text style={s.infoAide}>{t('calendrier.retablirAide')}</Text>}
              </View>
            )}

            {!!ev.note && (
              <View style={s.ficheNoteCarte}>
                <Text style={s.ficheRowLbl}>{t('calendrier.ficheNote')}</Text>
                <Text style={s.ficheNoteTxt}>{ev.note}</Text>
              </View>
            )}
            {ev.pieces?.length > 0 && (
              <View style={{ marginTop: 12 }}>
                <Text style={s.lbl}>{t('budget.pieces')}</Text>
                <PiecesJointes valeur={ev.pieces} dossier={`evenements/${ev._calId || calId}`} lecture />
              </View>
            )}
            {!edit && <Text style={s.infoAide}>{t('calendrier.lectureSeuleAide')}</Text>}
          </View>
        );
      })()}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ficheTete: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 14 },
  fichePastille: { width: 48, height: 48, borderRadius: 15, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  fichePastilleTxt: { fontSize: 24 },
  ficheTitre: { fontSize: 18, fontWeight: '800', color: UI.ink },
  ficheBadges: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 5 },
  ficheBadge: { paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: UI.champ },
  ficheBadgeTxt: { fontSize: 11, fontWeight: '700', color: UI.muted },
  ficheCarte: { borderWidth: 1, borderColor: UI.border, borderRadius: 13, backgroundColor: UI.card, paddingHorizontal: 13 },
  ficheRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 11 },
  ficheRowSep: { borderTopWidth: 0.5, borderTopColor: UI.border },
  ficheRowIc: { fontSize: 16, width: 24, textAlign: 'center' },
  ficheRowLbl: { fontSize: 12, color: UI.muted, fontWeight: '700', width: 76 },
  ficheRowVal: { flex: 1, fontSize: 14, color: UI.ink, fontWeight: '600', textAlign: 'right' },
  ficheNoteCarte: { marginTop: 12, backgroundColor: UI.champ, borderRadius: 12, padding: 12, gap: 4 },
  ficheNoteTxt: { fontSize: 13.5, color: UI.ink, lineHeight: 19 },
  ficheJours: { flexDirection: 'row', gap: 8, marginTop: 12, justifyContent: 'center' },
  ficheJour: { width: 34, height: 34, borderRadius: 17, borderWidth: 1, borderColor: UI.champBord, backgroundColor: UI.card, alignItems: 'center', justifyContent: 'center' },
  ficheJourOn: { backgroundColor: UI.accent, borderColor: UI.accent },
  ficheJourTxt: { fontSize: 13, color: UI.muted, fontWeight: '600' },
  ficheJourTxtOn: { color: '#fff', fontWeight: '800' },
  lbl: { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 12, marginBottom: 7 },
  excLigne: { backgroundColor: UI.card, borderRadius: 10, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 9, paddingHorizontal: 12 },
  excTxt: { fontSize: 13.5, color: UI.ink, fontWeight: '600' },
  infoAide: { fontSize: 11.5, color: UI.faint, marginTop: 14, fontStyle: 'italic' },
});
